const { isValidEmptyObject, isValidString, isValidNonEmptyString, isValidIzvidFile, isValidNonEmptyBase64, isValidBase64String, isNullOrUndefined, isValidNonNullInteger, isValidInteger } = require("./utils");
const { getZavarovalniskaStarostOsebe, getZacetekZavarovanja } = require('../common');

const INVALID_VALUE_ERROR = {
    invalidValue: true,
    hasError: true,
};
const REQUIRED_ERROR = {
    required: true,
    hasError: true,
};
const NO_ERROR = {
    hasError: false,
};

async function validateTextInput(
    {
        required
    },
    value, 
) {
    if (required) {
        if (!isValidNonEmptyString(value)) {
            return REQUIRED_ERROR;
        }
    }

    if (!isValidString(value))
        return INVALID_VALUE_ERROR;

    return NO_ERROR;
}

async function validateNazivBankeInput(
    {
        required
    },
    value,
    nazivBankeByIBANSuggestion,
) {
    if (required) {
        if (!isValidNonEmptyString(value)) {
            return REQUIRED_ERROR;
        }
    }

    if (!isValidString(value))
        return INVALID_VALUE_ERROR;
    else {
        if (nazivBankeByIBANSuggestion !== null) {
            if (
                nazivBankeByIBANSuggestion
                    .toUpperCase() 
                !== 
                value.toUpperCase()
            )
                return {
                    hasError: true,
                    nazivBankeDoesNotMatch: true,
                }
        }
    }

    return NO_ERROR;
}

async function validateBICInput(
    {
        required
    },
    value,
    BICByIBANSuggestion,
) {
    if (required) {
        if (!isValidNonEmptyString(value)) {
            return REQUIRED_ERROR;
        }
    }

    if (!isValidString(value))
        return INVALID_VALUE_ERROR;
    else {
        if (BICByIBANSuggestion !== null) {
            if (
                BICByIBANSuggestion
                    .toUpperCase() 
                !== 
                value.toUpperCase()
            )
                return {
                    hasError: true,
                    BICDoesNotMatch: true,
                }
        }
    }

    return NO_ERROR;
}

async function validateEmailInput(
    {
        required
    },
    value, 
) {
    if (required) {
        if (!isValidNonEmptyString(value)) {
            return REQUIRED_ERROR;
        }
    }

    if (isValidNonEmptyString(value)) {
        const invalidEmail 
            = !(value.toLowerCase()
                .match(/\S+@\S+\.\S+/));
        if (invalidEmail)
            return {
                invalidEmail: true,
                hasError: true,
            };
    }

    if (!isValidString(value))
        return INVALID_VALUE_ERROR;

    return NO_ERROR;
}

/**
 * 
 * @param {string} iban 
 * @returns {{ isValidIBAN: boolean, bankInfo: any }}
 */
async function getSuggestionBasedOnIBAN (
    validateIbanDataFunction,
    iban,
) {

    if (!isValidNonEmptyString(iban)) {
        return {
            isValidIBAN: false,
            bankInfo: null,    
        }
    }

    if (!iban.startsWith('SI56')) {
        return {
            isValidIBAN: true,
            bankInfo: null,    
        }
    }

    const response = await validateIbanDataFunction({
        iban
    });

    const bankInfo = response?.data?.bankInfo ?
    {
        bic: response?.data?.bankInfo?.bic || '',
        iban: response?.data?.bankInfo?.iban || '',
        nazivBanke: response?.data?.bankInfo?.nazivBanke || '',
        type: response?.data?.bankInfo?.type || '',
    } : null;

    return {
        isValidIBAN: !!(response.success),
        bankInfo,
    }
}

async function validateIBANInput(
    getSuggestionBasedOnIBANFunction,
    {
        required
    },
    value,
) {

    if (required) {
        if (!isValidNonEmptyString(value)) {
            return REQUIRED_ERROR;
        }
    }

    if (isValidNonEmptyString(value)) {
        if (!value.startsWith('SI56'))
            return NO_ERROR;
        else {         
            const invalidIBAN 
                = !(await getSuggestionBasedOnIBANFunction(value))
                    .isValidIBAN;

            if (invalidIBAN)
                return {
                    invalidIBAN: true,
                    hasError: true,
                };
        }
    }

    if (!isValidString(value))
        return INVALID_VALUE_ERROR;

    return NO_ERROR;
}

async function validatePotrditvenaKoda(
    isValidPotrditvenaKoda,
    {
        required
    },
    value,
) {

    if (required) {
        if (!isValidNonEmptyString(value)) {
            return REQUIRED_ERROR;
        }
    }

    if (isValidNonEmptyString(value)) {      
        const invalidValue 
            = !(await isValidPotrditvenaKoda(value));

        if (invalidValue)
            return INVALID_VALUE_ERROR;
    }

    if (!isValidString(value))
        return INVALID_VALUE_ERROR;

    return NO_ERROR;
}

function containsOnlyNumbers(str) {
    return /^\d+$/.test(str);
}

async function validateZipCodeInput(
    {
        required
    },
    value, 
) {
    if (required) {
        if (!isValidNonNullInteger(value)) {
            return REQUIRED_ERROR;
        }
    }

    if (isValidNonNullInteger(value)) {
        const invalidZipCode = 
            value.length !== 4
            || !containsOnlyNumbers(value);
        if (invalidZipCode)
            return {
                invalidZipCode: true,
                hasError: true,
            };
    }

    if (!isValidInteger(value))
        return INVALID_VALUE_ERROR;

    return NO_ERROR;
}

const isValidVatId = (value) => {
    let maxLen = 8; 

    if (value.length < 8) {
        return false;
    }

    let intValue = parseInt(value), maxDavcna = Math.pow(10, maxLen) - 1;
    if (isNaN(intValue)) {
        return false;
    }

    if (intValue > maxDavcna) {
        return false;
    }

    // Value format.
    let tabelaDavcna = [];
    for (let i = (maxLen - 1); i >= 0; i--) {
        tabelaDavcna[i] = intValue % 10;
        intValue -= tabelaDavcna[i];
        intValue /= 10;
    }

    let sestevekZmnozkov = 0;
    for (let pos = 0; pos < maxLen - 1; pos++) {
        sestevekZmnozkov = (maxLen - pos) * tabelaDavcna[pos] + sestevekZmnozkov;
    }

    let ostanekPriDeljenju = sestevekZmnozkov % 11;
    let kontrolnaStevilka = 11 - ostanekPriDeljenju;
    if (kontrolnaStevilka == 10) {
        kontrolnaStevilka = 0;
    }
    else if (kontrolnaStevilka == 11) {
        // invalid kontrolna stevilka
        return false;
    }

    if (kontrolnaStevilka != tabelaDavcna[maxLen - 1]) {
        // invalid kontrolna stevilka
        return false;
    }

    return true;
};

async function validateVatId1(
    {
        required
    },
    value, 
) {
    if (required) {
        if (!value) {
            return { 
                required: true,
                hasError: true,
            };
        }
    }

    if (value) {
        const invalidVatId = !(isValidVatId(value));
        if (invalidVatId)
            return {
                invalidVatId: true,
                hasError: true,
            };
    }

    return {
        hasError: false,
    };
}

const isValidDate = (obj) => {
    return obj instanceof Date && !isNaN(obj);
};

const isStarostDeltaValid = (inD, conf) => {
    let c = new Date(), diff = null;
    
    diff = c.getFullYear() - inD.getFullYear();

    if (conf.constraintType === 'PRAVA_STAROST') {
        // Se ni star toliko.
        let temp = new Date(c.getFullYear(), inD.getMonth(), inD.getDate());
        if (temp < c) {
            diff--;
        }
    }
    
    if (conf.deltaMin !== null && diff < conf.deltaMin) {
        return false;
    }

    // Is older then 30 days? We need it for bdca.
    let diffDays = Math.floor( (c.getTime() - inD.getTime()) / (24 * 3600 * 1000) );
    if (conf.deltaMinDays !== null && diffDays < conf.deltaMinDays) {
        return false;
    }

    if (conf.deltaMax !== null && diff > conf.deltaMax) {
        return false;
    }
    
    return true;
};

const isZavarovalniskaStarostDeltaValid = (inD, conf) => {
    if (
        conf.deltaZavarovalniskaStarostMin !== null
        || conf.deltaZavarovalniskaStarostMax !== null
    ) {
        const zavarovalniskaStarost = getZavarovalniskaStarostOsebe(
            getZacetekZavarovanja(),
            inD,
        );
        
        if (conf.deltaZavarovalniskaStarostMin !== null) {
            if (zavarovalniskaStarost < conf.deltaZavarovalniskaStarostMin)
                return false;
        }
        if (conf.deltaZavarovalniskaStarostMax !== null) {
            if (zavarovalniskaStarost > conf.deltaZavarovalniskaStarostMax)
                return false;
        }
    }
    
    return true;
};

async function validateDateInput(
    {
        required,
        conf,
    },
    value, 
) {
    const configuration = conf || {};
    if (required) {
        if (isNullOrUndefined(value))
            return REQUIRED_ERROR;
    }

    if (!isNullOrUndefined(value)) {
        const date = new Date(value);
        const invalidDate = !(isValidDate(date));
        if (invalidDate)
            return {
                invalidDate: true,
                hasError: true,
            };

        const invalidDelta
            = !isStarostDeltaValid(date, configuration);
        if (invalidDelta)
            return {
                invalidDelta: true,
                hasError: true,
            };

        const invalidZavarovalniskaStarostDelta
            = !isZavarovalniskaStarostDeltaValid(date, configuration);
        if (invalidZavarovalniskaStarostDelta)
            return {
                invalidZavarovalniskaStarostDelta: true,
                hasError: true,
            };
    }

    return NO_ERROR;
}

async function validateTnsDaNe(
    {
        required,
    },
    value, 
) {
    if (required) {
        if (
            value !== 'N' 
            && value !== 'D'
        ) return REQUIRED_ERROR;
    }

    if (
        value !== 'N' 
        && value !== 'D'
        && value !== undefined 
    )
        return INVALID_VALUE_ERROR;

    return NO_ERROR;
}

async function validateRadioDANERequiredDAField(
    {
        required,
    },
    value, 
) {
    if (required) {
        if (value !== 'DA') {
            return REQUIRED_ERROR;
        }
    }

    if (
        value !== undefined
        && value !== 'DA'
    ) return REQUIRED_ERROR;
        

    return NO_ERROR;
}

/**
 * 
 * @param {{ required: boolean }} param0 
 * @param {any} value 
 * @param {any[]} options 
 * @returns 
 */
async function validateRadioMultipleField(
    {
        required,
    },
    value,
    options,
) {
    if (required) {
        if (isNullOrUndefined(value)) {
            return REQUIRED_ERROR;
        }
    }

    if (!isNullOrUndefined(value)) {
        if (!isValidNonEmptyString(value))
            return INVALID_VALUE_ERROR;

        if (!options.some(option => option.value === value))
            return INVALID_VALUE_ERROR;
    }

    return NO_ERROR;
}

async function validateGenderInput(
    {
        required,
    },
    value, 
) {
    if (required) {
        if (
            value !== 'M' 
            && value !== 'Ž'
        ) return REQUIRED_ERROR;
    }

    if (
        value !== 'M' 
        && value !== 'Ž'
        && value !== undefined 
    ) return INVALID_VALUE_ERROR;

    return NO_ERROR;
}

async function validateRadioDANEWithExtraDataIfDA(
    {
        required,
    },
    value,
) {
    if (required) {
        if (
            !value
            && !value.DA 
            && !value.NE
        ) {
            return REQUIRED_ERROR;
        }
    }

    if (
        !value.DA 
        && !value.NE
        && value !== undefined
    ) return INVALID_VALUE_ERROR;

    if (
        value.NE && value.DA
    ) return INVALID_VALUE_ERROR;

    if (
        value.NE
        && !isValidEmptyObject(value.NE)
    ) return INVALID_VALUE_ERROR;

    if (value.DA) {
        if (typeof value.DA !== 'object')
            return INVALID_VALUE_ERROR;
        if (!isValidString(value.DA.Podrobnosti))
            return INVALID_VALUE_ERROR;
        if (
            isValidNonEmptyString(value.DA.Podrobnosti)
            && value.DA.Podrobnosti.length > 150
            ) return {
                hasError: true,
                max: true,
            };

        if (!Array.isArray(value.DA.Izvidi))
            return INVALID_VALUE_ERROR;
        for (const izvid of value.DA.Izvidi) {
            if (!isValidNonEmptyString(izvid.imeDokumenta))
                return INVALID_VALUE_ERROR;

            if (!isValidIzvidFile(izvid.vsebinaDokumenta))
                return INVALID_VALUE_ERROR;
        }

        if (
            !isValidNonEmptyString(value.DA.Podrobnosti)
            && ! value.DA.Izvidi.length
        ) {
            return {
                missingExtraData: true,
                hasError: true,
            };
        }
    }

    return NO_ERROR;
}

async function validateAddressInputGroup(
    {
        required,
    },
    value, 
) {

    const ulica 
        = await validateTextInput({ required }, value.ulica);
    const postnaStevilka 
        = await validateZipCodeInput({ required }, value.postnaStevilka);
    const kraj 
        = await validateTextInput({ required }, value.kraj);
    

    if (
        [ulica, postnaStevilka, kraj]
            .some(error => error.hasError)
    ) {
        return {
            hasError: true,
            ulica,
            postnaStevilka,
            kraj,    
        }
    }

    return {
        hasError: false,
    };
}

async function validatePrvaDiagnozaIdd2Q1potrjujemq2nezelim (
    {
        required,
    },
    value,
    field,
    idd2,
) {

    const radioMultipleErrors 
        = await validateRadioMultipleField(
            { required }, value, field.options);

    if (radioMultipleErrors.hasError)
        return radioMultipleErrors;

    if (
        value === 'q1potrjujem'
        && [
            idd2.q11.value,
            idd2.q12.value,
            idd2.q21.value,
            idd2.q22.value,
            idd2.q31.value,
            idd2.q32.value,
            idd2.q41.value,
            idd2.q42.value,
            idd2.q51.value,
            idd2.q52.value,
            idd2.q61.value,
            idd2.q62.value,
        ].every(daNe => daNe === 'N')
    ) {
        return {
            hasError: true,
            invalidIdd2q1potrjujem: true,
        };
    }

    if (
        value === 'q2nezelim'
        && [
            idd2.q11.value,
            idd2.q12.value,
            idd2.q21.value,
            idd2.q22.value,
            idd2.q31.value,
            idd2.q32.value,
            idd2.q41.value,
            idd2.q42.value,
            idd2.q51.value,
            idd2.q52.value,
            idd2.q61.value,
            idd2.q62.value,
        ].some(daNe => daNe === 'D')
    ) {
        return {
            hasError: true,
            invalidIdd2q2nezelim: true,
        };
    }

    return NO_ERROR;
}

async function validateCheckboxRequiredChecked(
    {
        required,
    },
    value, 
) {
    if (required) {
        if (
            value !== true 
            && value !== false
        ) {
            return { 
                required: true,
                hasError: true,
            };
        }
    }

    if (value !== true) {
        return {
            requiredChecked: true,
            hasError: true,
        };
    }

    return {
        hasError: false,
    };
}

async function validateCheckbox(
    {
        required,
    },
    value, 
) {
    if (required) {
        if (
            value !== true 
            && value !== false
        ) {
            return { 
                required: true,
                hasError: true,
            };
        }
    }

    if (
        value !== true
        && value !== false
        && value !== undefined
    ) return INVALID_VALUE_ERROR;

    return NO_ERROR;
}

async function validateSignature(
    {
        required,
    },
    value, 
) {
    if (required) {
        if (
            !isValidNonEmptyBase64(value)
        ) {
            return { hasError: true };
        }    
    }

    // TODO - validate signature size and validate signature base64 prefix
    if (!isValidBase64String(value)) {
        return { hasError: true };
    }

    return {
        hasError: false,
    };
}

async function validateTransakcijskiRacunInput (
    validateIbanDataFunction,
    {
        required,
    },
    value,
) {
    if (
        !value
        || typeof value !== 'object'
    ) {
        return {
            hasError: true,
            IBAN: INVALID_VALUE_ERROR,
            nazivBanke: INVALID_VALUE_ERROR,
            BIC: INVALID_VALUE_ERROR,
        };
    }
    
    const ibanData 
        = await validateIbanDataFunction(value.IBAN || null);
    const cacheFunction = () => ibanData;

    const IBAN = await validateIBANInput(
        cacheFunction, { required }, value.IBAN);
    const nazivBanke = await validateNazivBankeInput(
        { required }, 
        value.nazivBanke, 
        ibanData.bankInfo?.nazivBanke || null,
    );

    // console.log(ibanData);
    const BIC = await validateBICInput(
        { required }, 
        value.BIC, 
        ibanData.bankInfo?.bic || null,
    );

    if (IBAN.hasError || nazivBanke.hasError || BIC.hasError)
        return {
            hasError: true,
            IBAN,
            nazivBanke,
            BIC,
        };

    return NO_ERROR;
}

async function validateNacinObvescanjaPrvaDiagnoza (
    {
        required,
    },
    value,
) {
    if (
        !value
        || typeof value !== 'object'
    ) {
        return {
            hasError: INVALID_VALUE_ERROR,
        };
    }

    for (const checkbox of Object.values(value)) {
        const err = await validateCheckbox(
            { required: false }, checkbox);
        if (err.hasError) return err;
    }

    if (
        !Object.values(value).some(cb => cb)
    ) {
        return {
            hasError: true,
            atLeastOneRequired: true,
        }
    }

    return NO_ERROR;
}

/**
 * 
 * @param {string} value 
 * @returns {boolean}
 */
function isValidStevilkaZdravstveneKartice (value) {
    let expectedLength = 9;

    for (let i = 0; i < value.length; i++) {
        if (!isValidNonNullInteger(value.charAt(i))) {
            return false;
        }
    }

    if (value.length !== expectedLength) {
        return false;
    }

    const factors = [
        6,
        7,
        2,
        3,
        4,
        5,
        6,
        7
    ];

    let vsota = 0;
    for(let i = 0; i < (expectedLength - 1); i++) {
        vsota += Number(value[i]) * factors[i];
    }

    const ostanek = vsota % 11;
    let k = 11 - ostanek;
    if (expectedLength === 9 && (k === 10 || k === 11)) {
        k = 0;
    }

    if (k !== Number(value[expectedLength - 1])) {
        return false;
    }

    return true;
}

async function validateStevilkaZdravstveneKartice (
    {
        required
    },
    value, 
) {
    if (required) {
        if (!isValidNonEmptyString(value)) {
            return REQUIRED_ERROR;
        }
    }

    if (isValidNonEmptyString(value)) {
        const invalidStevilkaZdravstveneKartice 
            = !(isValidStevilkaZdravstveneKartice(value));
        if (invalidStevilkaZdravstveneKartice)
            return {
                invalidStevilkaZdravstveneKartice: true,
                hasError: true,
            };
    }

    return NO_ERROR;
}

async function validateFormField (
    isValidPotrditvenaKoda,
    getSuggestionBasedOnIBAN,
    field,
    formModel,
) {
    if (field.formType === 'radio') {
        const error = await validateTnsDaNe({ required: field.required }, field.value);
        return error;
    }
    else if (field.formType === 'gender-input') {
        const error = await validateGenderInput({ required: field.required }, field.value);
        return error;
    }
    else if (field.formType === 'text-input') {
        const error = await validateTextInput({ required: field.required }, field.value);
        return error;
    }
    else if (field.formType === 'date-input') {
        const error = await validateDateInput({ required: field.required, conf: field.conf }, field.value);
        return error;
    }
    else if (field.formType === 'auto-date-time-now') {
        const error = await validateDateInput({ required: field.required, conf: field.conf }, field.value);
        return error;
    }
    else if (field.formType === 'davcna-input') {
        const error = await validateVatId1({ required: field.required }, field.value);
        return error;
    }
    else if (field.formType === 'phone-input') {
        const error = await validateTextInput({ required: field.required }, field.value);
        return error;
    }
    else if (field.formType === 'email-input') {
        const error = await validateEmailInput({ required: field.required }, field.value);
        return error;
    }
    else if (field.formType === 'stevilka-zdravstvene-kartice-input') {
        const error = await validateStevilkaZdravstveneKartice({ required: field.required }, field.value);
        return error;
    }
    else if (field.formType === 'radio-multiple') {
        const error = await validateRadioMultipleField({ required: field.required }, field.value, field.options);
        return error;
    }
    else if (field.formType === 'radio-DA-NE-requiredDA') {
        const error = await validateRadioDANERequiredDAField({ required: field.required }, field.value);
        return error;
    }
    else if (field.formType === 'checkbox-required-checked') {
        const error = await validateCheckboxRequiredChecked({ required: field.required }, field.value);
        return error;
    }
    else if (field.formType === 'checkbox') {
        const error = await validateCheckbox({ required: field.required }, field.value);
        return error;
    }
    else if (field.formType === 'IBAN-input') {
        const error = await validateIBANInput(getSuggestionBasedOnIBAN, { required: field.required }, field.value);
        return error;
    }
    else if (field.formType === 'transkacijski-racun-input') {
        const error = await validateTransakcijskiRacunInput(
            getSuggestionBasedOnIBAN,
            { required: field.required }, 
            field.value
        );
        return error;
    }
    else if (field.formType === 'radio-DA-NE-with-extra-data-if-DA') {
        const error = await validateRadioDANEWithExtraDataIfDA(
            { required: field.required }, 
            field.value,
        );
        return error;
    }
    else if (field.formType === 'address-input-group') {
        const error = await validateAddressInputGroup({ required: field.required }, field.value);
        return error;
    }
    else if (field.formType === 'prva-diagnoza-idd2-q1potrjujemq2nezelim') {
        const error = await validatePrvaDiagnozaIdd2Q1potrjujemq2nezelim(
            { required: field.required }, field.value, field, formModel.idd2);
        return error;
    }
    else if (field.formType === 'signature') {
        const error = await validateSignature({ required: field.required }, field.value);
        return error;
    }
    else if (field.formType === 'potrditvena-koda') {
        const error = await validatePotrditvenaKoda(isValidPotrditvenaKoda, { required: field.required }, field.value);
        return error;
    }
    else if (field.formType === 'nacin-obvescanja-prvadiagnoza') {
        const error = await validateNacinObvescanjaPrvaDiagnoza({ required: field.required }, field.value);
        return error;
    } else {
        console.log(field);
        throw Error('Unknow field type');
    }
}

module.exports = {
    validateTextInput,
    validateEmailInput,
    getSuggestionBasedOnIBAN,
    validateIBANInput,
    validateZipCodeInput,
    validateVatId1,
    validateDateInput,
    validateTnsDaNe,
    validateRadioDANERequiredDAField,
    validateRadioMultipleField,
    validateGenderInput,
    validateRadioDANEWithExtraDataIfDA,
    validateAddressInputGroup,
    validateCheckboxRequiredChecked,
    validateCheckbox,
    validateFormField,
    validateTransakcijskiRacunInput,
    validateStevilkaZdravstveneKartice,
    validatePotrditvenaKoda,
};
